// Auto-computed risk / health flags from forecast summary.
// Reads diagnostics off `summarize(rows)`; safe against missing fields.

function fmtCompact(n) {
  const sign = n < 0 ? '−' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}k`;
  return `${sign}$${Math.round(a)}`;
}

function fmtPct(n, d = 1) {
  return `${(n * 100).toFixed(d)}%`;
}

function computeFlags(summary) {
  if (!summary) return [];
  const flags = [];

  const breakeven = summary.breakevenMonth ?? null;
  const wirexFloorPct = summary.wirexFloorAsPctRevenue ?? 0;
  const subPct = summary.proSubAsPctRevenue ?? 0;
  const marginPct = summary.grossMarginPct ?? 0;

  if (breakeven == null) {
    flags.push({
      tone: 'rose',
      title: 'No breakeven in 36 months',
      detail: `Cumulative operating cash ends at ${fmtCompact(summary.cumCashEnd ?? 0)} on this scenario.`,
    });
  } else if (breakeven > 30) {
    flags.push({
      tone: 'ice',
      title: `Late breakeven · M${breakeven}`,
      detail:
        'Investors pattern-match against 18-24 month payback windows. Expect slope questions.',
    });
  } else {
    flags.push({
      tone: 'mint',
      title: `Breakeven · M${breakeven}`,
      detail: 'Within typical investor timing windows.',
    });
  }

  if (wirexFloorPct > 0.1) {
    flags.push({
      tone: 'rose',
      title: 'High Wirex floor exposure',
      detail: `${fmtPct(wirexFloorPct)} of M36 revenue tops up Wirex's monthly minimum. Card usage and FX are not carrying enough weight in this scenario.`,
    });
  }

  if (subPct > 0.5) {
    flags.push({
      tone: 'ice',
      title: 'Subscription concentration',
      detail: `${fmtPct(subPct)} of M36 revenue is Pro subscription. Single-product if churn worsens.`,
    });
  } else if (subPct > 0 && subPct < 0.1) {
    flags.push({
      tone: 'ice',
      title: 'Subscription too thin',
      detail: `${fmtPct(subPct)} of revenue from paid plans. Investor narrative wants tiers carrying more weight.`,
    });
  }

  if (marginPct < 0.3 && marginPct >= 0) {
    flags.push({
      tone: 'rose',
      title: 'Low gross margin',
      detail: `${fmtPct(marginPct)} 3-year operating margin. Tighten subsidy adoption, CAC, opex, or wallet costs.`,
    });
  } else if (marginPct >= 0.5) {
    flags.push({
      tone: 'mint',
      title: 'Strong operating margin',
      detail: `${fmtPct(marginPct)} 3-year operating margin.`,
    });
  }

  return flags;
}

const TONE = {
  rose: {
    ring: 'border-rose-200',
    bg: 'bg-rose-50',
    heading: 'text-rose-800',
    body: 'text-rose-900',
  },
  ice: {
    ring: 'border-ice-200',
    bg: 'bg-ice-50',
    heading: 'text-ice-800',
    body: 'text-carbon-800',
  },
  mint: {
    ring: 'border-mint-300',
    bg: 'bg-mint-50',
    heading: 'text-mint-800',
    body: 'text-carbon-800',
  },
};

export default function RisksPanel({ summary }) {
  const flags = computeFlags(summary);
  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <div className="flex items-baseline justify-between mb-4">
        <p className="caption text-carbon-400">Risks · auto-computed</p>
        {flags.length > 0 && (
          <span className="label text-carbon-500 num-tabular">
            {flags.length} {flags.length === 1 ? 'flag' : 'flags'}
          </span>
        )}
      </div>

      {flags.length === 0 ? (
        <p className="body text-carbon-500">No flags at the current scenario.</p>
      ) : (
        <ul className="space-y-3">
          {flags.map((f) => {
            const t = TONE[f.tone];
            return (
              <li key={f.title} className={`rounded-box border ${t.ring} ${t.bg} px-4 py-3`}>
                <p className={`label ${t.heading}`}>{f.title}</p>
                <p className={`body mt-1 ${t.body}`}>{f.detail}</p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
